import { DeliverRequestSignature } from "./BMCClient";
import { OraclePublicCloudAuthenticatorSingleton, GetAuthorizationUrl, TokenRefreshResponse } from "./authenticator";
import { HgIndexedDBData } from "./HgIndexedDBData";
import { getHgCompatibleDbData, saveTokens, clearDb, setItem, fetchSecurityTokenBasedOnWhitelist } from "./indexedDb";
import { SESSION_TIMEOUT } from "../utils/publicErrorCode";
import { isTokenAboutToExpire, isTokenDead } from "../utils/tokenUtils";
import { fetchFluorineCrypto } from "./crypto";

export interface RefreshedSecurityToken {
  token: string;
}

export class TokenRefreshHandler {
  constructor(
    private getAuthorizationUrl: GetAuthorizationUrl,
    private securityTokenInCookieTenancyOverrides: string[],
  ) {}

  /**
   * Check if token has less than 10 minutes lifetime
   *
   * @param token Raw security token
   */
  private static isSecurityTokenAboutToExpire(token: string) {
    return isTokenAboutToExpire(token);
  }

  /**
   * Token has to be refreshed within 1 hour time frame and has a maximum of 24 hours lifespan
   * If token has been there for more than 1 hour, it is dead. We have to inform user to re-sign.
   *
   * @param token Raw security token
   */
  private static isSecurityTokenDead(token: string): boolean {
    return isTokenDead(token);
  }

  public async tryRefresh(
    realm: string,
    deliverRequest: DeliverRequestSignature,
    force?: boolean,
    regionName?: string,
  ): Promise<TokenRefreshResponse> {
    const hgCompatibleDbData: HgIndexedDBData = await getHgCompatibleDbData(realm);

    const securityToken = fetchSecurityTokenBasedOnWhitelist(
      hgCompatibleDbData,
      realm,
      this.securityTokenInCookieTenancyOverrides,
    );

    if (hgCompatibleDbData && securityToken && TokenRefreshHandler.isSecurityTokenDead(securityToken)) {
      // session is dead, notify upstream and clear AUTH material from db
      OraclePublicCloudAuthenticatorSingleton.reportError(SESSION_TIMEOUT);
      await clearDb(this.securityTokenInCookieTenancyOverrides, hgCompatibleDbData, realm);
      return;
    }

    const shouldNotRefresh =
      !hgCompatibleDbData ||
      !securityToken ||
      (!TokenRefreshHandler.isSecurityTokenAboutToExpire(securityToken) && !force);

    if (shouldNotRefresh) {
      // do nothing if token doesn't exist, or token is valid AND force refreshing is not allowed.
      return;
    }

    const refreshUrl = `${this.getAuthorizationUrl(regionName)}/authentication/refresh`;
    const request = new Request(refreshUrl, {
      headers: { "Content-Type": "application/json" },
      method: "POST",
      body: JSON.stringify({
        currentToken: securityToken,
      }),
    });

    try {
      const startTime = performance.now();
      const response = await deliverRequest(request);
      if (!response || !response.ok) {
        throw new Error(
          `Failed to refresh token. Status: "${response.status}", StatusText: "${response.statusText}", URL: "${refreshUrl}"`,
        );
      }
      const endTime = performance.now();
      const responseBody: RefreshedSecurityToken = await response.json();
      const tokenRefreshResponse: TokenRefreshResponse = {
        isTokenRenewed: true,
        duration: endTime - startTime,
      };
      // Identity token cannot be refreshed
      // Save the new security token but keep the old identity token
      await saveTokens(
        realm,
        responseBody.token,
        hgCompatibleDbData.identity_token,
        this.securityTokenInCookieTenancyOverrides,
      );
      return tokenRefreshResponse;
    } catch (err) {
      if (err && err.status) {
        throw new Error(
          `Failed to refresh token. Status: "${err.status}", StatusText: "${err.statusText}", URL: "${refreshUrl}"`,
        );
      } else {
        throw new Error(`${err}`);
      }
    }
  }
}

/**
 * Generate new private/public key pair
 * @param hgCompatibleDbData
 * @param realm
 * @returns new Private and Public key
 */
export async function generateNewKeyPair(hgCompatibleDbData: HgIndexedDBData, realm: string) {
  const cryptoObject = fetchFluorineCrypto();
  const envelope = await cryptoObject.generateKeyPair();
  await setItem(realm, {
    ...hgCompatibleDbData,
    private: envelope.materials.privateKey,
    public: envelope.materials.publicKey,
  });
  const a = await crypto.subtle.exportKey("spki", envelope.materials.publicKey);
  const publicKeyPem = `${btoa(String.fromCharCode(...new Uint8Array(a)))
    .match(/.{1,64}/g)
    .join("\n")}`;
  return publicKeyPem;
}
